import z from "zod";

import {
	type MetadataOperation,
	makeInitializeRequest,
	metadataOperation,
} from "../api-canvas-metadata";
import {
	transactionProcessedError,
	transactionProcessedReplacement,
	transactionProcessedSuccess,
} from "../api-canvas-metadata/TransactionProcessedEvent";
import {
	type CanvasSpecifier,
	DocumentMetadataSchema,
	type MessageRequestUnauthorized,
	type MessageStepsSinceRequest,
	type StepsSinceResponse,
	stepsSinceResponse,
} from "../api-types";
import { UnauthorizedError } from "../http";
import { accessToken, createHttpHeaders, headerAuth, post } from "../http/index";

export class MetadataCollabApiClient {
	private readonly fetchFn: (init?: RequestInit | undefined) => Promise<Response>;

	constructor(fetchFn: (init?: RequestInit | undefined) => Promise<Response>) {
		this.fetchFn = fetchFn;
	}

	public async initMetadataRoom(
		canvasSpecifier: CanvasSpecifier
	): Promise<MessageInitMetadataRoomResponseAccepted> {
		const headers = createHttpHeaders(accessToken());
		const response = await this.fetchFn(
			post(headers, initMetadataRoomRequest(headers[headerAuth] ?? "", canvasSpecifier))
		);

		if (response.status === 401) {
			throw new UnauthorizedError();
		}

		const json = await response.json();
		return messageIdempotentInitMetadataRoomResponseAccepted.parseAsync(json);
	}

	public async enqueueTransaction(
		transactionId: string,
		lastVersionSeen: number,
		operation: MetadataOperation
	): Promise<TransactionResponseBody> {
		const response = await this.fetchFn(
			post(
				createHttpHeaders(accessToken()),
				makeEnqueueTransactionEvent(transactionId, lastVersionSeen, operation)
			)
		);

		if (response.status === 401) {
			throw new UnauthorizedError();
		}

		const json = await response.json();
		return transactionResponseBody.parseAsync(json);
	}

	public async stepsSince(version: number): Promise<StepsSinceResponse> {
		const response = await this.fetchFn(
			post(createHttpHeaders(accessToken()), makeMessageStepsSinceRequest(version))
		);

		if (response.status === 401) {
			throw new UnauthorizedError();
		}

		const json = await response.json();
		return stepsSinceResponse.parseAsync(json);
	}
}

const initMetadataRoomRequest = (authorizationHeader: string, canvasSpecifier: CanvasSpecifier) => {
	return makeInitializeRequest({
		authorizationHeader,
		canvasSpecifier,
	});
};

function makeEnqueueTransactionEvent(
	transactionId: string | undefined,
	lastVersionSeen: number,
	operation: MetadataOperation
): EnqueueMetadataTransactionEvent {
	return { type: "enqueueTransaction", transactionId, lastVersionSeen, operation };
}

function makeMessageStepsSinceRequest(version: number): MessageStepsSinceRequest {
	return { type: "steps-since-request", version };
}

const messageIdempotentInitMetadataRoomResponseAccepted = z.object({
	type: z.literal("idempotent-init-metadata-room-response-accepted"),
	version: z.number(),
	canvasMetadata: DocumentMetadataSchema,
	page: z.unknown(),
});

export type MessageInitMetadataRoomResponseAccepted = z.infer<
	typeof messageIdempotentInitMetadataRoomResponseAccepted
>;

const messageIdempotentInitMetadataRoomResponseRejected = z.object({
	type: z.literal("idempotent-init-metadata-room-response-rejected"),
	version: z.number(),
});

export type MessageInitMetadataRoomResponseRejected = z.infer<
	typeof messageIdempotentInitMetadataRoomResponseRejected
>;

export const initMetadataInvalidResponse = z.object({
	error: z.string(),
	version: z.number(),
	canvasMetadata: z.unknown(),
});

export type InitMetadataInvalidResponse = z.infer<typeof initMetadataInvalidResponse>;

export type InitMetadataResponse =
	| MessageInitMetadataRoomResponseAccepted
	| MessageInitMetadataRoomResponseRejected
	| MessageRequestUnauthorized
	| InitMetadataInvalidResponse;

export const transactionResponseBody = z.object({
	lastAcknowledgedVersion: z.number(),
	lastTransactionProcessed: z.union([
		transactionProcessedSuccess,
		transactionProcessedError,
		transactionProcessedReplacement,
	]),
});
export type TransactionResponseBody = z.infer<typeof transactionResponseBody>;
export type TransactionResponse = TransactionResponseBody | MessageRequestUnauthorized;

/**
 * Enqueues a transaction to be processed by the canvas metadata collab server.
 */
export const enqueueMetadataTransactionEvent = z.object({
	type: z.literal("enqueueTransaction"),
	transactionId: z.optional(z.string()),
	lastVersionSeen: z.number(),
	operation: metadataOperation,
});

/**
 * Enqueues a transaction to be processed by the canvas metadata collab server.
 */
export type EnqueueMetadataTransactionEvent = z.infer<typeof enqueueMetadataTransactionEvent>;
